import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { externalShareHeaders } from "@/lib/externalShareToken";
import { consumeFreeQuota, FREE_QUOTA_MESSAGE } from "@/lib/plan/freeTier";


export interface PolishResult {
  polished: string;
  changed: boolean;
}

/**
 * Hjälper användaren att språkputsa fritext (kommentarer, motiveringar,
 * förklaringar) utan att innebörden ändras. AI:n körs server-side.
 */
export const useAiPolish = () => {
  const [isPolishing, setIsPolishing] = useState(false);

  const polish = useCallback(
    async (text: string, context?: string): Promise<PolishResult | null> => {
      const trimmed = (text || "").trim();
      if (!trimmed) {
        toast.info("Skriv något först", {
          description: "AI:n putsar bara text du redan skrivit.",
        });
        return null;
      }

      setIsPolishing(true);
      try {
        const quota = await consumeFreeQuota("ai");
        if (!quota.allowed) {
          toast.error("AI-kvoten är slut", { description: FREE_QUOTA_MESSAGE.ai });
          return null;
        }

        const { data, error } = await supabase.functions.invoke("ai-polish-text", {
          body: { text: trimmed, context },
          headers: externalShareHeaders(),
        });

        if (error) {
          let details = error.message;
          try {
            const ctx = (error as any)?.context;
            if (ctx?.text) {
              const body = await ctx.text();
              const parsed = JSON.parse(body);
              if (parsed?.error) details = parsed.error;
            }
          } catch {
            // behåll ursprungligt felmeddelande
          }
          toast.error("AI-hjälpen kunde inte köras", { description: details });
          return null;
        }

        if (data?.error) {
          toast.error("AI-hjälpen kunde inte köras", { description: data.error });
          return null;
        }

        const polished = String(data?.polished ?? "").trim();
        if (!polished) {
          toast.error("AI:n returnerade ingen text");
          return null;
        }

        return { polished, changed: polished !== trimmed };
      } catch (e: any) {
        toast.error("AI-hjälpen kunde inte köras", { description: e?.message });
        return null;
      } finally {
        setIsPolishing(false);
      }
    },
    []
  );

  return { polish, isPolishing };
};
